'use client';

import { c as _c } from "react/compiler-runtime";
import './nav-stack.scss';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { NavigationProps } from '@sola/schema/lib/schema.ts';
import { trackClick } from '@sola/tracking/components/datalayer.tsx';
import { Dialpad } from '../../atoms/dialpad/dialpad.tsx';
import { LinkList } from '../../atoms/link-list/link-list.tsx';
import { NavBlock } from '../nav-block/nav-block.tsx';
import { MobileAccountSection } from './nav-stack-mobile-account.tsx';
export type NavLevel = {
  items: NavigationProps[];
  parent?: NavigationProps;
};
type NavStackProps = {
  stack: NavLevel[];
  setStack: React.Dispatch<React.SetStateAction<NavLevel[]>>;
  showMyAccountSection?: boolean;
  className?: string;
};
export const NavStack: React.FC<NavStackProps> = t0 => {
  const $ = _c(37);
  const {
    stack,
    setStack,
    showMyAccountSection,
    className
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["articles"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const stackIndex = stack.length - 1;
  const activeLevel = stack[stackIndex];
  const activeNavItems = activeLevel.items;
  const {
    parent
  } = activeLevel;
  const isRootNav = stack.length === 1;
  let t2;
  if ($[1] !== activeNavItems || $[2] !== isRootNav) {
    t2 = !isRootNav && activeNavItems.every(_temp) && activeNavItems.every(_temp2);
    $[1] = activeNavItems;
    $[2] = isRootNav;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const useSmallLinkList = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[4] !== activeNavItems || $[5] !== className || $[6] !== isRootNav || $[7] !== parent || $[8] !== router || $[9] !== setStack || $[10] !== t || $[11] !== useSmallLinkList) {
    const hasIcons = activeNavItems.some(_temp3);
    if ($[17] !== activeNavItems) {
      t6 = activeNavItems.map(_temp4).join();
      $[17] = activeNavItems;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    if ($[19] !== className) {
      t7 = cn("NavStack py-comfy-sm flex flex-col gap-comfy-sm", className);
      $[19] = className;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    t3 = "flex flex-col gap-comfy-sm";
    if ($[21] !== isRootNav || $[22] !== parent?.link || $[23] !== parent?.title) {
      t4 = !isRootNav && <div className="p-comfy-sm sm:px-comfy-xl flex flex-col gap-snug-xl"><div className="NavStack__top-link-container pb-snug-md"><LinkStyle size="xl" noPadding={true} noUnderline={true} iconAfter="arrow-right"><Link href={parent?.link || "/"}>{parent?.title}</Link></LinkStyle></div></div>;
      $[21] = isRootNav;
      $[22] = parent?.link;
      $[23] = parent?.title;
      $[24] = t4;
    } else {
      t4 = $[24];
    }
    t5 = useSmallLinkList ? <div className="px-comfy-sm sm:px-comfy-xl"><LinkList item={{
        items: activeNavItems
      }} /></div> : <><ul className="gap-snug-xl flex flex-col m-none p-none">{activeNavItems.map(item => {
          const hasChildItems = item?.items?.length;
          const {
            displayVariant
          } = item;
          return <li className={cn("flex flex-col m-none p-none", {
            "NavStack__items--border mt-comfy-xs": displayVariant
          })} key={item.title}><div className={cn("flex justify-between", {
              "flex-col items-start": displayVariant,
              "items-center": !displayVariant
            })}><button type="button" onClick={() => {
                if (!hasChildItems && item.link) {
                  trackClick(item.title || item.description || "Unknown", item.link, "NavStack__link");
                  if (item?.target === "_blank") {
                    window.open(item?.link, item.target, "noopener,noreferrer");
                  } else {
                    router.push(item?.link);
                  }
                  return;
                }
                if (item.items) {
                  setStack(prev => [...prev, {
                    items: item.items as NavigationProps[],
                    parent: item
                  }]);
                }
              }} className={cn("NavStack__link flex justify-between items-center w-full px-comfy-sm sm:px-comfy-xl py-snug-xl", {
                "gap-snug-md": item.description,
                "py-comfy-sm": displayVariant
              })}><div className="flex gap-comfy-xs">{item?.icon && <ArkIcon iconName={item.icon} className="NavStack__icon" />}<div className={cn("flex flex-col", {
                    "NavStack__item-title--padding": hasIcons && !item?.icon,
                    "NavStack__item-title--padding--display-variant": displayVariant,
                    "gap-snug-md": item.description,
                    "pr-roomy-sm": !hasChildItems
                  })}><span className="flex gap-snug-xl items-center"><p className={cn("NavStack__text label label-lg label-neutral label-potent flex gap-snug-xl items-center", {
                        "label-xl label-potent": isRootNav
                      })}>{item.title}</p>{item.badge?.text && <span className="NavStack__badge"><Badge label={item.badge?.text} color={item.badge?.color} size="sm" /></span>}</span><p className="NavStack__description label-md label-moderate">{item.description}</p></div></div>{Boolean(hasChildItems) && !displayVariant && <div className={cn("NavStack__next-button ml-snug-md p-comfy-xs")}><ArkIcon iconName="chevron-right" /></div>}{isRootNav && !hasChildItems && !displayVariant && <div className={cn("NavStack__next-button ml-snug-md p-comfy-xs")}><ArkIcon iconName="arrow-right" /></div>}</button>{Boolean(hasChildItems) && displayVariant === "grid" && <div className="pt-snug-xl px-comfy-sm sm:px-comfy-xl"><Dialpad item={item} /></div>}{Boolean(hasChildItems) && displayVariant === "social" && <div className="pt-snug-xl px-comfy-sm sm:px-comfy-xl"><LinkList item={item} /></div>}</div></li>;
        })}</ul>{parent?.featuredArticles && <NavBlock featuredArticles={true} title={t("articles:featuredReads")} maxItems={2} mobile={true} />}{parent?.cta && <NavBlock items={[parent.cta]} mobile={true} />}</>;
    $[4] = activeNavItems;
    $[5] = className;
    $[6] = isRootNav;
    $[7] = parent;
    $[8] = router;
    $[9] = setStack;
    $[10] = t;
    $[11] = useSmallLinkList;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
  }
  let t8;
  if ($[25] !== t3 || $[26] !== t4 || $[27] !== t5) {
    t8 = <div className={t3}>{t4}{t5}</div>;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== isRootNav || $[30] !== showMyAccountSection) {
    t9 = isRootNav && showMyAccountSection && <MobileAccountSection />;
    $[29] = isRootNav;
    $[30] = showMyAccountSection;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t6 || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t10 = <div key={t6} className={t7}>{t8}{t9}</div>;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
};
function _temp(i_0) {
  return !i_0.items?.length;
}
function _temp2(i) {
  return !i.description;
}
function _temp3(i_1) {
  return Boolean(i_1.icon);
}
function _temp4(i_2) {
  return i_2.title;
}